import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { changePassword, setupMFA, verifyMFA, disableMFA } from '../lib/api';
import api from '../lib/api';
import { getRoleLabel } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Key, Shield, ShieldCheck, ShieldOff, User, Fingerprint, Trash2, Plus } from 'lucide-react';

export default function Profile() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const forceReset = searchParams.get('reset') === 'true';

  // Password change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // MFA
  const [mfaSetup, setMfaSetup] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaStep, setMfaStep] = useState('scan'); // 'scan' | 'verify' | 'complete'
  const [settingUpMFA, setSettingUpMFA] = useState(false);
  const [verifyingMFA, setVerifyingMFA] = useState(false);
  const [disablingMFA, setDisablingMFA] = useState(false);
  const [disableCode, setDisableCode] = useState('');

  // Passkeys
  const [passkeys, setPasskeys] = useState([]);
  const [passkeyName, setPasskeyName] = useState('');
  const [registeringPasskey, setRegisteringPasskey] = useState(false);
  const [deletingPasskeyId, setDeletingPasskeyId] = useState(null);
  const [loadingPasskeys, setLoadingPasskeys] = useState(true);

  // Load passkeys on mount
  React.useEffect(() => {
    loadPasskeys();
  }, []);

  const loadPasskeys = async () => {
    try {
      const { data } = await api.get('/passkeys');
      setPasskeys(data.passkeys || []);
    } catch {
      // ignore
    } finally {
      setLoadingPasskeys(false);
    }
  };

  /**
   * WebAuthn passkey registration flow:
   * 1. Request registration options (challenge) from server
   * 2. Call navigator.credentials.create() — triggers Touch ID / Face ID / security key
   * 3. Send the credential back to server for verification
   */
  const handleRegisterPasskey = async () => {
    if (!window.PublicKeyCredential) {
      toast.error('Passkeys are not supported in this browser');
      return;
    }
    setRegisteringPasskey(true);
    try {
      // Step 1: Get registration options
      const { data } = await api.post('/passkeys/register-options');
      const options = JSON.parse(data.options);

      // Convert base64url strings to ArrayBuffers
      options.challenge = _b64ToBuffer(options.challenge);
      options.user.id = _b64ToBuffer(options.user.id);
      if (options.excludeCredentials) {
        options.excludeCredentials = options.excludeCredentials.map(c => ({
          ...c, id: _b64ToBuffer(c.id),
        }));
      }

      // Step 2: Create credential (triggers biometric prompt)
      const credential = await navigator.credentials.create({ publicKey: options });

      // Step 3: Send credential to server
      const credentialJSON = {
        id: credential.id,
        rawId: _bufferToB64(credential.rawId),
        type: credential.type,
        response: {
          attestationObject: _bufferToB64(credential.response.attestationObject),
          clientDataJSON: _bufferToB64(credential.response.clientDataJSON),
        },
      };
      if (credential.response.getTransports) {
        credentialJSON.response.transports = credential.response.getTransports();
      }

      await api.post('/passkeys/register-complete', {
        credential: credentialJSON,
        name: passkeyName || 'My Passkey',
      });

      toast.success('Passkey registered successfully');
      setPasskeyName('');
      loadPasskeys();
      checkAuth();
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        toast.error('Passkey registration was cancelled');
      } else if (err.name === 'SecurityError') {
        toast.error('Passkey not allowed on this domain — ensure you\'re using HTTPS');
      } else if (err.name === 'InvalidStateError') {
        toast.error('This passkey is already registered on your account');
      } else if (err.name === 'NotSupportedError') {
        toast.error('No compatible authenticator found on this device');
      } else {
        toast.error(err.response?.data?.detail || err.message || 'Failed to register passkey');
      }
    } finally {
      setRegisteringPasskey(false);
    }
  };

  const handleDeletePasskey = async (id) => {
    if (!window.confirm('Remove this passkey? You won\'t be able to use it to sign in.')) return;
    setDeletingPasskeyId(id);
    try {
      await api.delete(`/passkeys/${id}`);
      toast.success('Passkey removed');
      loadPasskeys();
      checkAuth();
    } catch {
      toast.error('Failed to remove passkey');
    } finally {
      setDeletingPasskeyId(null);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (newPassword.length < 10) {
      toast.error('Password must be at least 10 characters');
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      toast.error('Password must contain at least one uppercase letter');
      return;
    }
    if (!/[a-z]/.test(newPassword)) {
      toast.error('Password must contain at least one lowercase letter');
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      toast.error('Password must contain at least one digit');
      return;
    }
    if (!/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(newPassword)) {
      toast.error('Password must contain at least one special character');
      return;
    }

    setChangingPassword(true);
    try {
      await changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      if (forceReset) {
        navigate('/');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSetupMFA = async () => {
    setSettingUpMFA(true);
    try {
      const response = await setupMFA();
      setMfaSetup(response.data);
      setMfaStep('scan');
    } catch (error) {
      toast.error('Failed to setup MFA');
    } finally {
      setSettingUpMFA(false);
    }
  };

  const handleVerifyMFA = async () => {
    if (!mfaCode || mfaCode.length !== 6) {
      toast.error('Enter a 6-digit code');
      return;
    }

    setVerifyingMFA(true);
    try {
      await verifyMFA(mfaCode);
      toast.success('MFA enabled successfully!');
      setMfaStep('complete');
      setMfaCode('');
      checkAuth();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code — check your authenticator app and try again');
    } finally {
      setVerifyingMFA(false);
    }
  };

  const handleDisableMFA = async () => {
    if (!disableCode || disableCode.length !== 6) {
      toast.error('Enter your current MFA code');
      return;
    }

    setDisablingMFA(true);
    try {
      await disableMFA(disableCode);
      toast.success('MFA disabled');
      setDisableCode('');
      checkAuth(); // Refresh user data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid code');
    } finally {
      setDisablingMFA(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="profile-page">
      {/* Profile Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Profile Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground text-xs">Name</Label>
                <p className="font-medium">{user?.name}</p>
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">Email</Label>
                <p className="font-medium">{user?.email}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground text-xs">Role</Label>
                <p className="font-medium">{getRoleLabel(user?.role)}</p>
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">MFA Status</Label>
                <p className="font-medium flex items-center gap-1">
                  {user?.mfa_enabled ? (
                    <>
                      <ShieldCheck className="w-4 h-4 text-emerald-600" />
                      Enabled
                    </>
                  ) : (
                    <>
                      <ShieldOff className="w-4 h-4 text-muted-foreground" />
                      Disabled
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Password Change */}
      <Card className={forceReset ? 'ring-2 ring-amber-500' : ''}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            Change Password
          </CardTitle>
          {forceReset && (
            <CardDescription className="text-amber-600">
              You are required to change your password before continuing.
            </CardDescription>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2">
              <Label>Current Password</Label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                data-testid="input-current-password"
              />
            </div>
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                data-testid="input-new-password"
              />
            </div>
            <div className="space-y-2">
              <Label>Confirm New Password</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                data-testid="input-confirm-password"
              />
            </div>
            <Button type="submit" disabled={changingPassword} data-testid="btn-change-password">
              {changingPassword ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Change Password
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* MFA Setup */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Two-Factor Authentication (MFA)
          </CardTitle>
          <CardDescription>
            Add an extra layer of security to your account using an authenticator app.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {user?.mfa_enabled ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-emerald-600">
                <ShieldCheck className="w-5 h-5" />
                <span className="font-medium">MFA is enabled</span>
              </div>
              <div className="border-t pt-4">
                <Label className="mb-2 block">Disable MFA</Label>
                <p className="text-sm text-muted-foreground mb-4">
                  Enter your current authenticator code to disable MFA.
                </p>
                <div className="flex gap-2">
                  <Input
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value)}
                    placeholder="123456"
                    maxLength={6}
                    className="w-32 font-mono text-center"
                    data-testid="input-disable-mfa-code"
                  />
                  <Button 
                    variant="destructive" 
                    onClick={handleDisableMFA}
                    disabled={disablingMFA}
                    data-testid="btn-disable-mfa"
                  >
                    {disablingMFA ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Disable MFA
                  </Button>
                </div>
              </div>
            </div>
          ) : mfaSetup ? (
            <div className="space-y-4">
              {/* Step indicator */}
              <div className="flex items-center gap-2 text-xs font-medium mb-2" data-testid="mfa-step-indicator">
                <span className={`px-2 py-1 rounded ${mfaStep === 'scan' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>1. Scan QR</span>
                <span className="text-muted-foreground">&#8594;</span>
                <span className={`px-2 py-1 rounded ${mfaStep === 'verify' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>2. Verify Code</span>
                <span className="text-muted-foreground">&#8594;</span>
                <span className={`px-2 py-1 rounded ${mfaStep === 'complete' ? 'bg-emerald-600 text-white' : 'bg-muted text-muted-foreground'}`}>3. Save Backup Codes</span>
              </div>

              {mfaStep === 'scan' && (
                <div className="space-y-4">
                  <p className="text-sm">
                    Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                  </p>
                  <img 
                    src={mfaSetup.qr_code} 
                    alt="MFA QR Code" 
                    className="w-48 h-48 border rounded"
                    data-testid="mfa-qr-code"
                  />
                  <div>
                    <p className="text-sm mb-2">Or enter this secret manually:</p>
                    <code className="bg-muted px-2 py-1 rounded text-sm font-mono">
                      {mfaSetup.secret}
                    </code>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button onClick={() => setMfaStep('verify')} data-testid="btn-mfa-next-verify">
                      Next: Verify Code
                    </Button>
                    <Button variant="outline" onClick={() => setMfaSetup(null)}>Cancel</Button>
                  </div>
                </div>
              )}

              {mfaStep === 'verify' && (
                <div className="space-y-4">
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
                    <strong>Important:</strong> MFA is NOT active until you verify a code below. Enter the 6-digit code shown in your authenticator app.
                  </div>
                  <div>
                    <Label className="mb-2 block">Enter the 6-digit code from your app</Label>
                    <div className="flex gap-2">
                      <Input
                        value={mfaCode}
                        onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="123456"
                        maxLength={6}
                        className="w-40 font-mono text-center text-lg tracking-widest"
                        data-testid="input-mfa-verify-code"
                        autoFocus
                        onKeyDown={(e) => e.key === 'Enter' && handleVerifyMFA()}
                      />
                      <Button 
                        onClick={handleVerifyMFA}
                        disabled={verifyingMFA || mfaCode.length !== 6}
                        data-testid="btn-verify-mfa"
                      >
                        {verifyingMFA ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                        Verify & Enable
                      </Button>
                    </div>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" onClick={() => setMfaStep('scan')}>Back</Button>
                    <Button variant="ghost" onClick={() => setMfaSetup(null)}>Cancel Setup</Button>
                  </div>
                </div>
              )}

              {mfaStep === 'complete' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-emerald-600">
                    <ShieldCheck className="w-5 h-5" />
                    <span className="font-medium">MFA has been enabled successfully!</span>
                  </div>
                  <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                    <strong>Save these backup codes now.</strong> You will not be able to see them again. Each code can only be used once if you lose access to your authenticator app.
                  </div>
                  <div className="grid grid-cols-2 gap-2 bg-muted p-3 rounded" data-testid="mfa-backup-codes">
                    {mfaSetup.backup_codes?.map((code, i) => (
                      <code key={i} className="text-sm font-mono">{code}</code>
                    ))}
                  </div>
                  <Button onClick={() => { setMfaSetup(null); setMfaStep('scan'); }} data-testid="btn-mfa-done">
                    I've Saved My Codes — Done
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <div>
              {user?.mfa_setup_pending && (
                <div className="p-3 mb-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800" data-testid="mfa-pending-warning">
                  <strong>Incomplete setup:</strong> You started MFA setup previously but never verified a code. Click below to start fresh.
                </div>
              )}
              <p className="text-sm text-muted-foreground mb-4">
                Use an authenticator app like Google Authenticator or Authy to get time-based codes.
              </p>
              <Button onClick={handleSetupMFA} disabled={settingUpMFA} data-testid="btn-setup-mfa">
                {settingUpMFA ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}
                Setup MFA
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Passkeys (WebAuthn / FIDO2) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Fingerprint className="w-5 h-5" />
            Passkeys
          </CardTitle>
          <CardDescription>
            Sign in with Touch ID, Face ID, Windows Hello, or a security key. Passkeys bypass the TOTP code requirement.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Registered passkeys list */}
          {loadingPasskeys ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading passkeys...
            </div>
          ) : passkeys.length > 0 ? (
            <div className="space-y-2 mb-4">
              {passkeys.map((pk) => (
                <div key={pk.id} className="flex items-center justify-between p-3 rounded-lg border bg-muted/30" data-testid={`passkey-row-${pk.id}`}>
                  <div className="flex items-center gap-3">
                    <Fingerprint className="w-4 h-4 text-emerald-600" />
                    <div>
                      <p className="text-sm font-medium">{pk.name}</p>
                      <p className="text-xs text-muted-foreground">
                        Added {pk.created_at?.slice(0, 10)}
                        {pk.last_used_at && ` — Last used ${pk.last_used_at.slice(0, 10)}`}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost" size="icon"
                    onClick={() => handleDeletePasskey(pk.id)}
                    disabled={deletingPasskeyId === pk.id}
                    data-testid={`delete-passkey-${pk.id}`}
                  >
                    {deletingPasskeyId === pk.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4 text-destructive" />}
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground mb-4">No passkeys registered yet.</p>
          )}

          {/* Register new passkey */}
          {window.PublicKeyCredential ? (
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label className="text-xs mb-1 block">Passkey Name</Label>
                <Input
                  value={passkeyName}
                  onChange={(e) => setPasskeyName(e.target.value)}
                  placeholder="e.g. MacBook Pro, iPhone, YubiKey"
                  className="h-9"
                  data-testid="input-passkey-name"
                />
              </div>
              <Button onClick={handleRegisterPasskey} disabled={registeringPasskey} data-testid="btn-register-passkey">
                {registeringPasskey ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Add Passkey
              </Button>
            </div>
          ) : (
            <p className="text-sm text-amber-600">
              Your browser doesn't support passkeys. Use a modern browser like Chrome, Safari, or Edge.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


/* ---- WebAuthn base64url helpers ---- */
function _b64ToBuffer(base64url) {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const pad = base64.length % 4 === 0 ? '' : '='.repeat(4 - (base64.length % 4));
  const binary = atob(base64 + pad);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function _bufferToB64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
